import {
	Button,
	Dialog,
	DialogContent,
	DialogActions,
	DialogTitle,
	Typography,
	Checkbox,
	Box,
	BDSProvider,
	Alert,
	FormControlLabel,
	Grid,
} from '@barracuda-internal/bds-core-v2';
import React, { ReactElement, useState } from 'react';
import { useApiRequest } from 'src/hooks/useApi';
import { useAppToolchain } from 'src/hooks/useAppToolchain';
import useAuth from 'src/hooks/useAuth';
import { DateTime } from 'luxon';

function SavedData(): ReactElement {
	const { auth } = useAuth();
	const { api } = useAppToolchain();
	const [doDelete, setDoDelete] = useState(false);
	const [doCancelDelete, setDoCancelDelete] = useState(false);
	const [hasDeleteJob, setHasDeleteJob] = useState(true);
	const [warnWin, setWarnWin] = useState(false);
	const [acceptWarn, setAcceptWarn] = useState(false);
	const [isAllowed, setIsAllowed] = useState(false);

	useApiRequest(
		auth &&
			((signal) =>
				api.getAccountAssignment(auth.accountDetails.account.id, {
					obj: 'obj_all',
					operation: 'delete',
				})),
		[auth],
		([status, resp]) => {
			if (resp && resp.data) {
				let currentUserId = auth.user.id;
				let assignments = resp.data;
				assignments.forEach((ass) => {
					if (ass.subject == currentUserId) {
						setIsAllowed(true);
					}
				});
			}
		},
	);
	const [jobStatus, jd] = useApiRequest(
		auth &&
			hasDeleteJob &&
			(() => api.getDeleteAccJob(auth.accountDetails.account.id)),
		[auth, hasDeleteJob],
		(jobData) => {
			setHasDeleteJob(false);
		},
	);
	const onClose = () => {
		setWarnWin(false);
	};
	const [updateStatus, req, error] = useApiRequest(
		doDelete &&
			(() =>
				api.deleteAccount(auth.accountDetails.account.id, {
					delay: '00.00:05:00',
				})),
		[doDelete],
		() => {
			setWarnWin(false);
			setDoDelete(false);
			setHasDeleteJob(true);
		},
	);
	const [cancelJobStatus] = useApiRequest(
		doCancelDelete &&
			(() => api.cancelDeleteAccJob(auth.accountDetails.account.id)),
		[doCancelDelete],
		() => {
			setDoCancelDelete(false);
			setHasDeleteJob(true);
		},
	);

	if (!isAllowed) {
		return <></>;
	}

	return (
		<BDSProvider>
			<>
				<Dialog open={warnWin} onClose={onClose}>
					<DialogTitle>
						<Typography variant="h2">Delete saved Data?</Typography>
					</DialogTitle>
					<DialogContent>
						{error ? (
							<Alert
								severity="error"
								style={{ marginBottom: '24px' }}
							>
								There was an error deleting the tenant. Please
								try again.
							</Alert>
						) : null}
						<Alert
							severity="warning"
							style={{ marginBottom: '24px' }}
						>
							You are about to delete inspector meta-data,
							redactions and authorization keys to your source
							data
						</Alert>
						<Box sx={{ mb: 3, ml: 2, mr: 4 }}>
							<FormControlLabel
								control={
									<Checkbox
										checked={acceptWarn}
										onChange={(e, checked) =>
											setAcceptWarn(checked)
										}
										sx={{ mr: 1 }}
									/>
								}
								label="I understand that deleting saved data is permanent and cannot be undone after 24 hours and that I will now be immediately logged out."
							/>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button
							// isLoading={updateStatus === 'RUNNING'}
							bdsType="interactiveSubtle"
							onClick={() => setWarnWin(false)}
						>
							Cancel
						</Button>
						<Button
							isLoading={updateStatus === 'RUNNING'}
							bdsType="interactiveEmphasis"
							disabled={!acceptWarn}
							onClick={() => {
								setDoDelete(true);
							}}
						>
							Delete
						</Button>
					</DialogActions>
				</Dialog>
				<Box>
					<Typography variant="h3" color="textPrimary" sx={{ mb: 2 }}>
						Saved Data
					</Typography>
					<Box sx={{ ml: 2, mt: 2 }}>
						<Typography
							variant="h4"
							color="textPrimary"
							sx={{ mb: 2 }}
						>
							Offboard Data Inspector
						</Typography>
						{jobStatus == 'RESOLVED' && (
							<Alert
								style={{
									display: 'flex',
									alignItems: 'center',
								}}
								severity="error"
							>
								Deletion process is set to start at &nbsp;
								{DateTime.fromISO(jd.deleteTime).toLocaleString(
									DateTime.DATE_FULL,
								)}{' '}
								&nbsp;
								{DateTime.fromISO(jd.deleteTime).toLocaleString(
									DateTime.TIME_WITH_SHORT_OFFSET,
								)}
								<Button
									size="small"
									onClick={() => {
										setDoCancelDelete(true);
									}}
									sx={{ ml: 2 }}
								>
									Stop deletion
								</Button>
							</Alert>
						)}
						<Grid
							container
							spacing={1}
							alignItems="flex-start"
							style={{ marginTop: '16px' }}
						>
							<Grid item xs={3}>
								<Button
									variant="outlined"
									onClick={() => {
										setWarnWin(true);
									}}
									disabled={jobStatus == 'RESOLVED'}
									color="secondary"
								>
									Delete
								</Button>
							</Grid>
							<Grid item xs={9}>
								<Typography variant="body1" color="textPrimary">
									Offboarding will delete Data Inspector
									meta-data, redactions and authorization keys
									to your source data. Upon deletion, you will
									be logged out and have a 24 hour grace
									period to log back in and stop the deletion
									process.
								</Typography>

								<Typography
									variant="body1"
									color="textPrimary"
									sx={{ mt: 2 }}
								>
									Deleting Data Inspector Tenant will have no
									impact on the original source data.
								</Typography>
							</Grid>
						</Grid>
					</Box>
				</Box>
			</>
		</BDSProvider>
	);
}

export default SavedData;
